// =============================================================================
// immunity-service.ts — pure-function Immunity state slice
//
// WEB.4.B: Implements the Immunity resolver's state backing store.
// Pattern: mirrors aura-manager.ts (register/discard/getByOwner) and
// facility-ownership-service.ts (typed errors, assertFiniteInt, deepFreeze).
//
// Shapes:
//   ImmunityInstance — one registered immunity entry.
//   ImmunityState    — collection of active immunities (frozen array).
//
// Key behaviors:
//   register         — adds a new ImmunityInstance (no dedupe by design; each
//                      card play creates a distinct instance).
//   discard          — removes entry by instanceId.
//   consumeIfMatches — checks whether an incoming effect (kind + optionally
//                      tileIndex) is intercepted by any active immunity for the
//                      given ownerSeat.  If it matches a charge-consuming entry,
//                      decrements chargesRemaining; removes entry when it reaches 0.
//                      Returns { intercepted: boolean, newState }.
//   getByOwner       — all immunities for a given seat.
//
// Pure-function discipline: NO module-level state, NO singletons. State is
// threaded as an explicit argument and returned in the result. All exposed
// state objects are deep-frozen.
// =============================================================================

import type { CardEffectKind } from "@gdg/shared";
import { deepFreeze } from "../_freeze.js";
import type { ActionLog, JsonValue } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/**
 * One active immunity entry.
 *
 * ownerSeat       — seat whose facilities / energy are shielded.
 * tileIndex       — null = whole-seat immunity (e.g. "IMMUNE TO ATTACK/SABOTAGE").
 *                   integer = single-tile immunity ("PROTECT 1 FACILITY").
 * againstKinds    — effect kinds this immunity intercepts (non-empty; validated at register).
 * sourceCardName  — the card that created this immunity (for log attribution).
 * chargesRemaining — how many intercepts remain. When it reaches 0 the entry is removed.
 *                   Use Number.POSITIVE_INFINITY for "permanent" cards — in practice
 *                   we encode with a large sentinel value (see IMMUNITY_PERMANENT_CHARGES).
 * isPassive       — true = always-active aura-style; false = reacts and is consumed.
 */
export interface ImmunityInstance {
  readonly instanceId: number;
  readonly ownerSeat: number;
  readonly tileIndex: number | null;
  readonly againstKinds: ReadonlyArray<CardEffectKind>;
  readonly sourceCardName: string;
  readonly chargesRemaining: number;
  readonly isPassive: boolean;
}

export interface ImmunityState {
  readonly entries: ReadonlyArray<ImmunityInstance>;
}

/** Sentinel for "permanent" immunity (infinite charges). */
export const IMMUNITY_PERMANENT_CHARGES = 999;

// ---------------------------------------------------------------------------
// Typed error
// ---------------------------------------------------------------------------

export class ImmunityError extends Error {
  readonly code: "INVALID_SEAT" | "INVALID_TILE" | "INVALID_CHARGES" | "EMPTY_KINDS" | "NOT_FOUND";

  constructor(code: ImmunityError["code"], message: string) {
    super(message);
    this.name = "ImmunityError";
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface ImmunityConsumeResult {
  readonly intercepted: boolean;
  readonly newState: ImmunityState;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createImmunityState(): ImmunityState {
  return deepFreeze({ entries: [] });
}

// ---------------------------------------------------------------------------
// register
// ---------------------------------------------------------------------------

/**
 * Register a new ImmunityInstance into state.
 * Validates: ownerSeat ≥ 0, tileIndex finite int when not null, chargesRemaining > 0,
 * againstKinds non-empty.
 * No dedupe — each card play creates a distinct entry (caller is responsible for
 * discarding stale entries when the card leaves play).
 */
export function registerImmunity(
  state: ImmunityState,
  log: ActionLog,
  instance: ImmunityInstance,
): { newState: ImmunityState; log: ActionLog } {
  assertFiniteInt(instance.instanceId, "registerImmunity instanceId");
  assertFiniteInt(instance.ownerSeat, "registerImmunity ownerSeat");
  if (instance.ownerSeat < 0) {
    throw new ImmunityError(
      "INVALID_SEAT",
      `registerImmunity: ownerSeat must be ≥ 0, got ${instance.ownerSeat}`,
    );
  }
  if (instance.tileIndex !== null) {
    assertFiniteInt(instance.tileIndex, "registerImmunity tileIndex");
    if (instance.tileIndex < 0) {
      throw new ImmunityError(
        "INVALID_TILE",
        `registerImmunity: tileIndex must be ≥ 0 or null, got ${instance.tileIndex}`,
      );
    }
  }
  if (
    !Number.isFinite(instance.chargesRemaining) ||
    instance.chargesRemaining <= 0 ||
    !Number.isInteger(instance.chargesRemaining)
  ) {
    throw new ImmunityError(
      "INVALID_CHARGES",
      `registerImmunity: chargesRemaining must be a positive integer, got ${instance.chargesRemaining}`,
    );
  }
  if (instance.againstKinds.length === 0) {
    throw new ImmunityError(
      "EMPTY_KINDS",
      "registerImmunity: againstKinds must have at least one entry",
    );
  }

  const frozen = deepFreeze(instance);
  const newEntries = Object.freeze([...state.entries, frozen]);
  const newState: ImmunityState = deepFreeze({ entries: newEntries });

  const nextLog = log.append({
    kind: "immunity.registered",
    data: {
      instanceId: instance.instanceId,
      ownerSeat: instance.ownerSeat,
      tileIndex: instance.tileIndex,
      againstKinds: [...instance.againstKinds],
      sourceCardName: instance.sourceCardName,
      chargesRemaining: instance.chargesRemaining,
    } as JsonValue,
  });

  return { newState, log: nextLog };
}

// ---------------------------------------------------------------------------
// discard
// ---------------------------------------------------------------------------

/**
 * Remove an immunity entry by instanceId.
 * Returns removed=false (with warning) when instanceId not found.
 */
export function discardImmunity(
  state: ImmunityState,
  log: ActionLog,
  instanceId: number,
): { newState: ImmunityState; log: ActionLog; removed: boolean } {
  assertFiniteInt(instanceId, "discardImmunity instanceId");

  const idx = state.entries.findIndex((e) => e.instanceId === instanceId);
  if (idx === -1) {
    console.warn(`[ImmunityService] discardImmunity: instanceId=${instanceId} not found.`);
    return { newState: state, log, removed: false };
  }

  const removed = state.entries[idx];
  const newEntries = Object.freeze([
    ...state.entries.slice(0, idx),
    ...state.entries.slice(idx + 1),
  ]);
  const newState: ImmunityState = deepFreeze({ entries: newEntries });

  const nextLog = log.append({
    kind: "immunity.discarded",
    data: {
      instanceId,
      ownerSeat: removed?.ownerSeat ?? -1,
      sourceCardName: removed?.sourceCardName ?? "?",
    } as JsonValue,
  });

  return { newState, log: nextLog, removed: true };
}

// ---------------------------------------------------------------------------
// consumeIfMatches
// ---------------------------------------------------------------------------

/**
 * Check whether an incoming effect is intercepted by any active immunity
 * owned by `ownerSeat`.
 *
 * Matching rules:
 *   1. entry.ownerSeat === ownerSeat  (the defended player)
 *   2. entry.againstKinds includes effectKind
 *   3. If tileIndex is supplied (per-tile interception check) AND entry.tileIndex is
 *      not null → entry.tileIndex must === tileIndex.
 *      If entry.tileIndex is null → whole-seat immunity — matches any tile.
 *      If tileIndex is not supplied (seat-level check) → matches both null and integer entries.
 *
 * When matched:
 *   - Decrement chargesRemaining.
 *   - If chargesRemaining reaches 0 → discard the entry.
 *   - Returns { intercepted: true, newState, log }.
 *
 * When not matched:
 *   - Returns { intercepted: false, newState: state, log } (no state change).
 *
 * Multiple immunity entries may match; only the FIRST match is consumed per
 * call (one card play = one interception per effect). The caller will call
 * this once per incoming effect.
 */
export function consumeIfMatches(
  state: ImmunityState,
  log: ActionLog,
  ownerSeat: number,
  effectKind: CardEffectKind,
  tileIndex?: number | undefined,
): ImmunityConsumeResult {
  assertFiniteInt(ownerSeat, "consumeIfMatches ownerSeat");

  for (let i = 0; i < state.entries.length; i++) {
    const entry = state.entries[i];
    if (entry === undefined) continue;

    // Must belong to the right owner.
    if (entry.ownerSeat !== ownerSeat) continue;

    // Must cover the incoming effect kind.
    if (!(entry.againstKinds as ReadonlyArray<string>).includes(effectKind)) continue;

    // Tile scope check.
    if (tileIndex !== undefined && entry.tileIndex !== null) {
      // Per-tile entry — must match the specific tile.
      if (entry.tileIndex !== tileIndex) continue;
    }
    // entry.tileIndex === null → whole-seat: matches any tileIndex (or no tileIndex).

    // Found a match — consume one charge.
    const newCharges = entry.chargesRemaining - 1;

    let newEntries: ReadonlyArray<ImmunityInstance>;
    if (newCharges <= 0) {
      // Exhausted — remove entry.
      newEntries = Object.freeze([...state.entries.slice(0, i), ...state.entries.slice(i + 1)]);
    } else {
      // Decrement and update.
      const updated: ImmunityInstance = deepFreeze({
        ...entry,
        chargesRemaining: newCharges,
      });
      newEntries = Object.freeze([
        ...state.entries.slice(0, i),
        updated,
        ...state.entries.slice(i + 1),
      ]);
    }

    const newState: ImmunityState = deepFreeze({ entries: newEntries });

    const nextLog = log.append({
      kind: "immunity.consumed",
      data: {
        instanceId: entry.instanceId,
        ownerSeat: entry.ownerSeat,
        tileIndex: entry.tileIndex,
        effectKind,
        sourceCardName: entry.sourceCardName,
        chargesConsumed: 1,
        chargesRemaining: newCharges,
        exhausted: newCharges <= 0,
      } as JsonValue,
    });

    return { intercepted: true, newState, log: nextLog };
  }

  // No match.
  return { intercepted: false, newState: state, log };
}

// ---------------------------------------------------------------------------
// getByOwner
// ---------------------------------------------------------------------------

/**
 * All active immunities owned by the given seat.
 */
export function getImmunitiesByOwner(
  state: ImmunityState,
  ownerSeat: number,
): ReadonlyArray<ImmunityInstance> {
  assertFiniteInt(ownerSeat, "getImmunitiesByOwner ownerSeat");
  return Object.freeze(state.entries.filter((e) => e.ownerSeat === ownerSeat));
}
